import React, { useRef, useState } from 'react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { Upload, X, Loader2, GripVertical, Star } from 'lucide-react';
import ImageCropperModal from './ImageCropperModal';

const fileToDataUrl = (file) => new Promise((resolve, reject) => {
  const r = new FileReader();
  r.onload = () => resolve(r.result);
  r.onerror = reject;
  r.readAsDataURL(file);
});

/**
 * MultiImageUpload — gallery editor.
 * value: array of image URLs / data-URLs
 * onChange: (nextArray) => void
 * max: maximum number of images (default 6)
 * heroValue / onSetHero: optional — lets a gallery image be "starred" as the hero.
 */
export default function MultiImageUpload({
  value = [],
  onChange,
  max = 6,
  heroValue,
  onSetHero,
  label = 'Gallery images',
  testId = 'gallery-upload',
  aspect = 4 / 5,
}) {
  const [uploading, setUploading] = useState(false);
  const [dragIdx, setDragIdx] = useState(null);
  const [overIdx, setOverIdx] = useState(null);
  const [cropQueue, setCropQueue] = useState([]); // array of data-URLs waiting to be cropped
  const inputRef = useRef(null);

  const images = value || [];
  const canAdd = images.length < max;

  const pick = () => inputRef.current?.click();

  const handleFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    const slots = max - images.length;
    if (slots <= 0) {
      toast.error(`Maximum ${max} images.`);
      return;
    }
    const chosen = files.slice(0, slots);
    const queue = [];
    for (const file of chosen) {
      if (file.size > 8 * 1024 * 1024) { toast.error(`${file.name}: max 8 MB`); continue; }
      try {
        queue.push(await fileToDataUrl(file));
      } catch { /* skip */ }
    }
    if (queue.length) setCropQueue(queue);
    if (inputRef.current) inputRef.current.value = '';
  };

  const uploadCropped = async (dataUrl) => {
    setUploading(true);
    try {
      const blob = await (await fetch(dataUrl)).blob();
      const form = new FormData();
      form.append('file', blob, 'upload.jpg');
      const { data } = await api.post('/admin/upload', form, { headers: { 'Content-Type': 'multipart/form-data' } });
      onChange([...(value || []), data.data_url]);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      // Advance the queue
      setCropQueue((q) => q.slice(1));
    }
  };

  const cancelCurrentCrop = () => setCropQueue((q) => q.slice(1));

  const remove = (idx) => {
    const next = images.filter((_, i) => i !== idx);
    onChange(next);
  };

  const move = (from, to) => {
    if (from === to || from == null || to == null) return;
    const next = [...images];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    onChange(next);
  };

  const onDragStart = (i) => setDragIdx(i);
  const onDragOver = (e, i) => { e.preventDefault(); setOverIdx(i); };
  const onDrop = (e, i) => {
    e.preventDefault();
    move(dragIdx, i);
    setDragIdx(null);
    setOverIdx(null);
  };

  return (
    <div data-testid={testId}>
      {label && (
        <div className="h-eyebrow mb-1.5 flex items-center justify-between">
          <span>{label}</span>
          <span className="opacity-70 normal-case tracking-normal text-[10px]">{images.length}/{max} · drag to reorder</span>
        </div>
      )}

      <div className="grid grid-cols-3 md:grid-cols-4 gap-2.5">
        {images.map((src, i) => {
          const isHero = onSetHero && heroValue && heroValue === src;
          const isOver = overIdx === i;
          return (
            <div
              key={`${src.substring(0, 24)}-${i}`}
              draggable
              onDragStart={() => onDragStart(i)}
              onDragOver={(e) => onDragOver(e, i)}
              onDrop={(e) => onDrop(e, i)}
              onDragEnd={() => { setDragIdx(null); setOverIdx(null); }}
              className="relative aspect-square rounded-lg overflow-hidden group cursor-move transition"
              style={{
                border: `2px solid ${isHero ? 'var(--heritage-plum)' : isOver ? 'var(--heritage-plum)' : 'rgba(75,24,56,0.15)'}`,
                background: '#FBF8F1',
                transform: dragIdx === i ? 'scale(0.97)' : 'scale(1)',
                opacity: dragIdx === i ? 0.6 : 1,
              }}
              data-testid={`${testId}-image-${i}`}
            >
              <img src={src} alt={`gallery ${i + 1}`} className="w-full h-full object-cover pointer-events-none" />

              {/* Order badge */}
              <span
                className="absolute top-1 left-1 rounded-full w-5 h-5 text-[10px] flex items-center justify-center h-serif"
                style={{ background: 'rgba(75,24,56,0.9)', color: '#F6F0E4' }}
              >
                {i + 1}
              </span>

              {/* Grip indicator */}
              <span className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <GripVertical size={14} style={{ color: '#F6F0E4', filter: 'drop-shadow(0 1px 1px rgba(0,0,0,0.5))' }} />
              </span>

              {/* Hover controls */}
              <div className="absolute inset-x-0 bottom-0 p-1 flex items-center justify-between gap-1 opacity-0 group-hover:opacity-100 transition-opacity"
                style={{ background: 'linear-gradient(to top, rgba(75,24,56,0.9), transparent)' }}>
                {onSetHero && (
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); onSetHero(src); }}
                    title={isHero ? 'Current hero' : 'Set as hero'}
                    className="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[10px]"
                    style={{ background: isHero ? 'var(--heritage-plum)' : 'rgba(255,255,255,0.85)', color: isHero ? '#F6F0E4' : 'var(--heritage-plum)' }}
                    data-testid={`${testId}-set-hero-${i}`}
                  >
                    <Star size={10} fill={isHero ? '#F6F0E4' : 'none'} /> {isHero ? 'Hero' : 'Set hero'}
                  </button>
                )}
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); remove(i); }}
                  title="Remove"
                  className="inline-flex items-center rounded-md p-1 text-[10px]"
                  style={{ background: 'rgba(154,44,44,0.9)', color: '#F6F0E4' }}
                  data-testid={`${testId}-remove-${i}`}
                >
                  <X size={11} />
                </button>
              </div>
            </div>
          );
        })}

        {canAdd && (
          <button
            type="button"
            onClick={pick}
            disabled={uploading}
            className="aspect-square rounded-lg border-2 border-dashed flex flex-col items-center justify-center gap-1.5 hover:bg-[rgba(75,24,56,0.04)] transition"
            style={{ borderColor: 'rgba(75,24,56,0.3)', color: 'var(--heritage-plum)' }}
            data-testid={`${testId}-add`}
          >
            {uploading ? (
              <>
                <Loader2 size={20} className="animate-spin opacity-70" />
                <span className="text-[10px] opacity-70">Uploading…</span>
              </>
            ) : (
              <>
                <Upload size={20} className="opacity-70" />
                <span className="text-[10px] opacity-80">Add image</span>
              </>
            )}
          </button>
        )}
      </div>

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple
        onChange={handleFiles}
        className="hidden"
      />

      {cropQueue.length > 0 && (
        <ImageCropperModal
          src={cropQueue[0]}
          defaultRatio={aspect}
          onCancel={cancelCurrentCrop}
          onConfirm={uploadCropped}
          testId={`${testId}-cropper`}
        />
      )}

      <div className="text-[10px] opacity-70 mt-2" style={{ color: 'var(--heritage-plum)' }}>
        PNG, JPG, WebP · max 8 MB before crop · up to {max} total · drag to reorder{onSetHero ? ' · hover an image to set it as the hero' : ''}.
      </div>
    </div>
  );
}
